import { useState, useEffect } from 'react';
import { Button } from '@/components/common/Button';
import { Alert } from '@/components/common/Alert';
import { useTranslation } from 'react-i18next';
import { checkTerminalStatus, processTerminalPayment } from '@/services/api';

interface PaymentTerminalProps {
  amount: number;
  onPaymentComplete?: () => void;
}

const PaymentTerminal = ({ amount, onPaymentComplete }: PaymentTerminalProps) => {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    checkTerminalStatus();
  }, []);

  const handleStartPayment = async () => {
    setStatus('processing');
    setError('');

    try {
      const isTerminalReady = await checkTerminalStatus();
      
      if (!isTerminalReady) {
        setError(t('terminalNotReady'));
        setStatus('idle');
        return;
      }

      const success = await processTerminalPayment(amount, 'KM');

      if (!success) {
        setError(t('paymentFailed'));
        setStatus('failed');
        return;
      }

      setStatus('success');
      onPaymentComplete?.();

    } catch (error) {
      setError(t('paymentError'));
      setStatus('failed');
    }
  };

  return (
    <div className="space-y-4">
      {error && <Alert variant="destructive">{error}</Alert>}
      
      {status === 'idle' && (
        <Button onClick={handleStartPayment} variant="outline" size="lg" className="w-full">
          {t('startPayment')}
        </Button>
      )}

      {status === 'processing' && (
        <div className="text-center">
          <p>{t('processingPayment')}</p>
          <p>{t('followTerminalInstructions')}</p>
        </div>
      )}

      {status === 'success' && (
        <div className="text-center text-green-600">
          {t('paymentSuccessful')}
        </div>
      )}

      {status === 'failed' && (
        <Button onClick={handleStartPayment} variant="outline" size="lg" className="w-full">
          {t('retryPayment')}
        </Button>
      )}
    </div>
  );
};

export default PaymentTerminal;